import {HistoryDateContentProps} from '@di-shared/features/history/date/HistoryDateContent/index';
import {ErrorBoundary} from 'react-error-boundary';
import {ERROR} from '@di-shared/const';
import React, {Suspense, useCallback} from 'react';
import useTripDetailKr from '@di-shared/features/history/date/useTripDetail.kr';
import {PermissionTypeKr, TripOrderByType} from '@di-shared/api/di/type';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import useIntersectionObserver from '@shared/useHandleObserver';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';
import {formatNumberWithCommas} from '@di-shared/utils';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';

export default function ({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          isMonthChange={isMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  const {isEmptyTrip} = useTripDetailKr({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('T10067')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {convertDistanceSymbol, convertDistanceValue, convertSpeedSymbol, convertSpeedValue} = useUnitConverter();
  const {checkAvailableComponent} = useAvailableComponentKr({vin});
  const {fetchNextPage, hasNextPage, isFetchingNextPage, summaryData, totalDrivingHours, totalDrivingMinutes, formattedTripDetails} =
    useTripDetailKr({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 새로운 주행기록 스크롤
  const handleObserver = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const {observerRef} = useIntersectionObserver(handleObserver, {threshold: 0});

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{formatNumberWithCommas(convertDistanceValue(summaryData.totalDrvDist))}</span>
              <span className="trip_summary_unit">{convertDistanceSymbol(summaryData.totalDrvDist)}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="trip_summary_value">{totalDrivingHours}</span>
                  <span className="trip_summary_unit">{t('T10054')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="trip_summary_value">{totalDrivingMinutes}</span>
                  <span className="trip_summary_unit">{t('T10055')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="trip_summary_value">{t('T10202')}</span>}
            </div>
          </div>
          {checkAvailableComponent(PermissionTypeKr.SAFETY_SCORE) && (
            <>
              <div className="divider divider_vertical" />
              <div className="trip_summary_item">
                <span className="trip_summary_title">{t('T10057')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{formatNumberWithCommas(summaryData.safetyDrvScore)}</span>
                  <span className="trip_summary_unit">{t('T10056')}</span>
                  {summaryData.safetyDrvScoreDiff >= 0 ? (
                    <span className="trip_summary_compare type_up">
                      <span
                        className="ic ic_di_count_up"
                        role="img"
                        aria-label={t('T10059')}
                      />
                      <span className="trip_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                    </span>
                  ) : (
                    <span className="trip_summary_compare type_down">
                      <span
                        className="ic ic_di_count_down"
                        role="img"
                        aria-label={t('T10058')}
                      />
                      <span className="trip_summary_value">{formatNumberWithCommas(Math.abs(summaryData.safetyDrvScoreDiff))}</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {formattedTripDetails.map((trip) => {
        const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
        const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);

        if (!ignitionOnDate || !ignitionOffDate) {
          throw new Error('ignitionOnDate or ignitionOffDate error');
        }

        return (
          <>
            <div
              className="trip_info"
              key={trip.ignOnTime}
            >
              <span className="blind">trip info detail</span>
              <div className="trip_info_head">
                <span className="trip_info_item">
                  {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
                  &nbsp;(
                  {!trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes && t('T10202')}
                  {!!trip.intervalIgnitionHours && t('T30103_$hour', {hour: `${trip.intervalIgnitionHours}`})}
                  {!!trip.intervalIgnitionHours && !!trip.intervalIgnitionMinutes && <>&nbsp;</>}
                  {!!trip.intervalIgnitionMinutes && t('T30104_$minute', {minute: trip.intervalIgnitionMinutes})})
                </span>
                {trip.drvDist < 1 ? (
                  <span className="trip_info_item">{t('T20341')}</span>
                ) : (
                  <span className="trip_info_item">
                    {formatNumberWithCommas(convertDistanceValue(trip.drvDist))}
                    {convertDistanceSymbol(trip.drvDist)}
                  </span>
                )}
              </div>
            </div>
            <ul className="statistics_record_list">
              <li className="statistics_record_item">
                <span className="statistics_record_key">{t('T10204')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{trip.idleTime}</span>
                  <span className="statistics_record_unit">{t('T10055')}</span>
                </span>
              </li>
              <div className="divider divider_vertical"></div>
              <li className="statistics_record_item">
                <span className="statistics_record_key">{t('T10205')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.avgSpeed))}</span>
                  <span className="statistics_record_unit">{convertSpeedSymbol(trip.avgSpeed)}</span>
                </span>
              </li>
              <div className="divider divider_vertical"></div>
              <li className="statistics_record_item">
                <span className="statistics_record_key">{t('T10206')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.maxSpeed))}</span>
                  <span className="statistics_record_unit">{convertSpeedSymbol(trip.maxSpeed)}</span>
                </span>
              </li>
              <div className="divider divider_vertical"></div>
            </ul>
          </>
        );
      })}
      {(isFetchingNextPage || hasNextPage) && (
        <div
          className="page_loading_box"
          ref={observerRef}
        >
          <div className="loading_lottie">
            <LottieSelector
              id="img_json_loading_black_60f"
              kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              loop={true}
              autoplay={true}
              style={{width: 24, height: 24}}
            />
          </div>
          <span className="loading_text">{t('T10185')}</span>
        </div>
      )}
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('T10008')}</span>
        </div>
        <div className="trip_summary_list type_multi_line type_error skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="trip_info skeleton"
        style={{height: '126px'}}
      >
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultDistanceSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('T10008')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line type_error">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10052')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10053')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10054')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10055')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('T10057')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('T10056')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="trip_info">
        <span className="blind">trip info detail</span>
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}
